/**
 * @file CoreApp.tsx
 *
 * @description Root application component providing all global providers and context.
 * Sets up routing, state management, and development tools.
 *
 * @module core
 *
 * @domain core
 *
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { Suspense, useState, useRef, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { RouterProvider } from "react-router-dom";

import { OuraDataProvider } from "@/domains/health/oura/contexts/OuraDataContext";
// Debug System
import { DebugOverlay } from "@/domains/logging";
import { FilterContextProvider } from "@/domains/productivity/timetracq";
import { router } from "@/routes";
import {
  LayoutProvider,
  FavoritesProvider,
  SearchProvider,
  HeaderProvider,
} from "@/shared/contexts";

import { SidebarProvider } from "./layout";

const queryClient = new QueryClient();

const LOCAL_STORAGE_KEY = "devtools-button-position";

/**
 * CoreApp - Root application component
 *
 * Provides:
 * - React Query for server state management
 * - Header context for global header state
 * - Search context for global search
 * - Layout context for layout management
 * - Favorites context for favorites management
 * - Sidebar context for sidebar state
 * - Filter context for TimeTracQ filters
 * - Oura data context for health data
 * - Debug overlay for development
 * - React Query Devtools (development only)
 * - Draggable devtools button (development only)
 *
 * @example
 * ```tsx
 * <CoreApp />
 * ```
 *
 * @returns {JSX.Element} Root application component
 *
 */
export const CoreApp = () => {
  const [showDevtools, setShowDevtools] = useState(false);
  const buttonReference = useRef<HTMLButtonElement>(null);

  // Load devtools button position from localStorage or use default
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) {
      return { x: 20, y: 200 };
    }

    try {
      const parsed = JSON.parse(stored) as unknown;
      if (
        parsed &&
        typeof parsed === "object" &&
        "x" in parsed &&
        "y" in parsed &&
        typeof (parsed as { x?: unknown }).x === "number" &&
        typeof (parsed as { y?: unknown }).y === "number"
      ) {
        return {
          x: (parsed as { x: number }).x,
          y: (parsed as { y: number }).y,
        };
      }
    } catch {
      // Ignore malformed localStorage content and fall back to default
    }

    return { x: 20, y: 200 };
  });

  // Setup draggable devtools button (development only)
  useEffect(() => {
    const button = buttonReference.current;
    if (!button) {
      return;
    }

    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      offsetX = e.clientX - position.x;
      offsetY = e.clientY - position.y;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }
      const newPosition = {
        x: e.clientX - offsetX,
        y: e.clientY - offsetY,
      };
      setPosition(newPosition);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPosition));
    };

    const onMouseUp = () => {
      isDragging = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    button.addEventListener("mousedown", onMouseDown);
    return () => {
      button.removeEventListener("mousedown", onMouseDown);
    };
  }, [position]);

  return (
    <QueryClientProvider client={queryClient}>
      <HeaderProvider>
        <SearchProvider>
          <LayoutProvider>
            <FavoritesProvider>
              <SidebarProvider>
                <FilterContextProvider>
                  <OuraDataProvider>
                    <Suspense
                      fallback={<div className="p-4 text-slate-100">⏳ Lade Anwendung …</div>}
                    >
                      <RouterProvider router={router} />
                    </Suspense>
                    <Toaster position="bottom-right" />

                    {/* Debug System */}
                    <DebugOverlay />
                  </OuraDataProvider>
                </FilterContextProvider>
              </SidebarProvider>
            </FavoritesProvider>
          </LayoutProvider>
        </SearchProvider>
      </HeaderProvider>

      {import.meta.env.DEV ? (
        <>
          <button
            className="z-50 bg-zinc-800 text-white text-xs px-3 py-1 rounded shadow hover:bg-zinc-700 cursor-move"
            onClick={() => {
              setShowDevtools(true);
            }}
            ref={buttonReference}
            style={{ left: position.x, position: "fixed", top: position.y }}
          >
            🛠 Devtools
          </button>

          {showDevtools ? (
            <div
              className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
              onClick={() => {
                setShowDevtools(false);
              }}
            >
              <div
                className="absolute bottom-0 left-0 w-full h-[400px] bg-zinc-900 border-t border-zinc-700"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="flex justify-end p-2">
                  <button
                    className="text-xs text-white bg-zinc-700 hover:bg-zinc-600 px-2 py-1 rounded"
                    onClick={() => {
                      setShowDevtools(false);
                    }}
                  >
                    ✖ schließen
                  </button>
                </div>
                <ReactQueryDevtoolsPanel />
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </QueryClientProvider>
  );
};
